import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";

import {
  getEnabledSections,
  getProfileSections,
  isClientProfile,
  type SectionId,
} from "@/config/uiConfig";
import { useAccount } from "@/hooks/useAccount";
import { isBlockingAuthentication } from "@/auth/session";

interface AuthorizationContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  /** Profile the interface is shaped for, as the API resolves it (a demo preview included). */
  profile: string | undefined;
  isClientProfile: boolean;
  authorizedSections: ReadonlySet<SectionId>;
  isSectionAvailable: (sectionId: SectionId) => boolean;
  canAccessSection: (sectionId: SectionId) => boolean;
  retry: () => void;
}

const AuthorizationContext = createContext<AuthorizationContextValue | null>(null);

export function AuthorizationProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const account = useAccount();
  const value = useMemo<AuthorizationContextValue>(() => {
    const displayedProfile = account.data?.profile;
    const enabledSections = getEnabledSections(account.data?.demo_enabled === true);
    const authorizedSections = new Set<SectionId>(
      account.data
        ? getProfileSections(displayedProfile).filter((sectionId) =>
            enabledSections.includes(sectionId),
          )
        : [],
    );

    return {
      isAuthenticated: auth.isAuthenticated,
      isLoading: isBlockingAuthentication(auth) || (auth.isAuthenticated && account.isPending),
      error: account.error,
      profile: displayedProfile,
      isClientProfile: isClientProfile(displayedProfile),
      authorizedSections,
      isSectionAvailable: (sectionId) => enabledSections.includes(sectionId),
      canAccessSection: (sectionId) => authorizedSections.has(sectionId),
      retry: () => void account.refetch(),
    };
  }, [account, auth.isAuthenticated, auth.isLoading, auth.activeNavigator, auth.user]);

  return <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>;
}

export function useAuthorization() {
  const context = useContext(AuthorizationContext);

  if (!context) {
    throw new Error("useAuthorization must be used within an AuthorizationProvider");
  }

  return context;
}

/** Like {@link useAuthorization}, for components that may render outside an AuthorizationProvider. */
export function useOptionalAuthorization() {
  return useContext(AuthorizationContext);
}
